/**
 * Web Push subscription helper for Pranidomu.cz providers.
 *
 * Flow:
 *   1. User clicks "Zapnout notifikace" → `enablePush(api)` is called.
 *   2. We register `/push-sw.js` at scope "/", request Notification permission,
 *      fetch the server's VAPID public key, and create a PushSubscription.
 *   3. The subscription JSON is sent to POST /api/push/subscribe where the
 *      backend stores it keyed by endpoint (upsert).
 *
 * The helper intentionally performs permission/subscribe inside the user gesture
 * so iOS 16.4+ grants permission when the site runs as a PWA from the Home Screen.
 */

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function base64UrlToBytes(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

const IOS_STANDALONE_HINT =
  'Na iPhonu/iPadu nejprve přidej web na plochu (Sdílet → Přidat na plochu), potom aplikaci spusť z ikony a znovu klikni na „Zapnout notifikace".';

export async function enablePush(api) {
  if (!pushSupported()) {
    throw new Error("Tento prohlížeč push notifikace nepodporuje. Zkus prosím Chrome na Androidu nebo Safari na iOS 16.4+.");
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;
  if (isIOS && !isStandalone) {
    throw new Error(IOS_STANDALONE_HINT);
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Oprávnění zamítnuto. Povol notifikace v nastavení prohlížeče a zkus to znovu.");
  }

  const registration = await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;

  const { data: vapid } = await api.get("/push/vapid-public-key");
  if (!vapid?.key || !vapid?.configured) {
    throw new Error("Push notifikace nejsou na serveru nakonfigurovány. Kontaktuj administrátora.");
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(vapid.key),
    });
  }

  const payload = subscription.toJSON();
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
  });

  return true;
}

export async function disablePush(api) {
  if (!pushSupported()) return false;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return false;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return false;
  const payload = subscription.toJSON();
  try {
    await api.post("/push/unsubscribe", { endpoint: payload.endpoint, keys: payload.keys });
  } catch (_e) { /* ignore server error — still unsubscribe locally */ }
  await subscription.unsubscribe();
  return true;
}

export async function sendTestPush(api) {
  const { data } = await api.post("/push/test");
  return data;
}

export async function getPushState() {
  if (!pushSupported()) return { supported: false, subscribed: false, permission: "denied" };
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    return {
      supported: true,
      subscribed: !!subscription,
      permission: Notification.permission,
    };
  } catch (_e) {
    return { supported: true, subscribed: false, permission: Notification.permission };
  }
}
